import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body2, Heading6 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { RichIcon } from '@ui/components/RichIcon'
import { AlertIcon } from '@ui/icons/Alert.icon'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'
import { CirclePlusIcon } from '@ui/icons/CirclePlus.icon'
import { CloseIcon } from '@ui/icons/Close.icon'

import {
  isAccountCompanyInfoDocumentAllowedUpload,
  isAccountDocumentManagement1MonthExpired,
  isAccountDocumentManagement2MonthExpired,
} from '@utils/AccountAccess/accountDocumentStatuses'
import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path'

import { isAccountPendingDocumentVerificationStatusType } from '@model/AccountDetailedStatus'
import { DocumentCategoryType } from '@model/DocumentCategories'
import {
  DocumentCategoryStatusDto,
  isAdditionalDocumentsGeneralStatus,
  isDocumentsApprovedGeneralStatus,
  isDocumentsRequiredGeneralStatus,
  isMoreDocumentsRequiredGeneralStatus,
  isRejectedDocumentsGeneralStatus,
  isVerificationPendingGeneralStatus,
} from '@model/DocumentGeneralStatusDto'
import { DocumentStatus } from '@model/DocumentStatus'
import { NameDto } from '@model/NameDto'

import { Loading } from '@shared/components/Loading/Loading'

import { DocumentManagementCategoryModal } from './modals/DocumentManagementCategory.modal'

import styles from './UploadDocuments.module.scss'

const getStatusForDocumentCategory = (
  documents: DocumentCategoryStatusDto[] = [],
  documentCategory: DocumentCategoryType,
  documentStatus: DocumentStatus
): boolean => {
  return documents.some(
    (document) => document.category.id === documentCategory && document.status.id === documentStatus
  )
}

export const UploadDocuments: FC = () => {
  const { t } = useTranslation()
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()
  const apiClient = useApiClient(ClientApiClient)

  const navigate = useNavigate()

  const { data: generalStatusData, isLoading } = useQuery({
    queryKey: queryKeys.document.generalStatus(account?.id, locale),
    queryFn: () => apiClient.getGeneralStatus({ clientId: account?.id, languageId: locale }),
  })

  const categoryModal = useModal(DocumentManagementCategoryModal)

  const handleIdentificationUploadClick = () => {
    if (account?.visibilityConfiguration?.hasNCI) {
      navigate(`${CommonPaths.DocumentManagement}/identification`)
      return
    }

    if (!account?.visibilityConfiguration?.hasNCI) {
      categoryModal.open({
        documentCategoryType: DocumentCategoryType.Personal,
        onSelectOption: handleIdentificationDocumentCategory,
      })
    }
  }

  const handleIdentificationDocumentCategory = (
    documentCategory: NameDto,
    documentCategoryType: NameDto
  ) => {
    navigate(`${CommonPaths.DocumentManagement}/identification`, {
      state: {
        documentCategory,
        documentCategoryType,
      },
    })
  }

  const handleProofOfAddressDocumentCategory = (
    documentCategory: NameDto,
    documentCategoryType: NameDto
  ) => {
    navigate(`${CommonPaths.DocumentManagement}/proof-of-address`, {
      state: {
        documentCategory,
        documentCategoryType,
      },
    })
  }

  const handleAdditionalDocumentCategory = (
    documentCategory: NameDto,
    documentCategoryType: NameDto
  ) => {
    navigate(`${CommonPaths.DocumentManagement}/additional`, {
      state: {
        documentCategory,
        documentCategoryType,
      },
    })
  }

  const handlePaymentsCategory = (documentCategory: NameDto, documentCategoryType: NameDto) => {
    navigate(`${CommonPaths.DocumentManagement}/payments`, {
      state: {
        documentCategory,
        documentCategoryType,
      },
    })
  }

  const handleCompanyDocumentCategory = (
    documentCategory: NameDto,
    documentCategoryType: NameDto
  ) => {
    navigate(`${CommonPaths.DocumentManagement}/company-proof`, {
      state: {
        documentCategory,
        documentCategoryType,
      },
    })
  }

  const handleCompanyProofUploadClick = () =>
    categoryModal.open({
      documentCategoryType: DocumentCategoryType.Corporate,
      onSelectOption: handleCompanyDocumentCategory,
    })
  const handleAdditionalDocumentsUploadClick = () =>
    categoryModal.open({
      documentCategoryType: DocumentCategoryType.Additional,
      onSelectOption: handleAdditionalDocumentCategory,
    })
  const handleProofOfAddressUploadClick = () =>
    categoryModal.open({
      documentCategoryType: DocumentCategoryType.Address,
      onSelectOption: handleProofOfAddressDocumentCategory,
    })
  const handlePaymentsUploadClick = () =>
    categoryModal.open({
      documentCategoryType: DocumentCategoryType.Payment,
      onSelectOption: handlePaymentsCategory,
    })

  const isCompanyDocumentApproved = getStatusForDocumentCategory(
    generalStatusData?.categoryStatuses,
    DocumentCategoryType.Corporate,
    DocumentStatus.Approved
  )

  const isCompanyDocumentPending = getStatusForDocumentCategory(
    generalStatusData?.categoryStatuses,
    DocumentCategoryType.Corporate,
    DocumentStatus.PendingApproval
  )

  const isAdditionalDocumentApproved = getStatusForDocumentCategory(
    generalStatusData?.categoryStatuses,
    DocumentCategoryType.Additional,
    DocumentStatus.Approved
  )

  const isAdditionalDocumentPending = getStatusForDocumentCategory(
    generalStatusData?.categoryStatuses,
    DocumentCategoryType.Additional,
    DocumentStatus.PendingApproval
  )

  const isPersonalDocumentApproved = getStatusForDocumentCategory(
    generalStatusData?.categoryStatuses,
    DocumentCategoryType.Personal,
    DocumentStatus.Approved
  )

  const isPersonalDocumentPending = getStatusForDocumentCategory(
    generalStatusData?.categoryStatuses,
    DocumentCategoryType.Personal,
    DocumentStatus.PendingApproval
  )

  const isAddressDocumentApproved = getStatusForDocumentCategory(
    generalStatusData?.categoryStatuses,
    DocumentCategoryType.Address,
    DocumentStatus.Approved
  )

  const isAddressDocumentPending = getStatusForDocumentCategory(
    generalStatusData?.categoryStatuses,
    DocumentCategoryType.Address,
    DocumentStatus.PendingApproval
  )

  const hasActivePersonalDocument = generalStatusData?.categoryStatuses.some(
    (document) =>
      document.category.id === DocumentCategoryType.Personal &&
      (isPersonalDocumentApproved || isPersonalDocumentPending)
  )

  const hasActiveAddressDocument = generalStatusData?.categoryStatuses.some(
    (document) =>
      document.category.id === DocumentCategoryType.Address &&
      (isAddressDocumentApproved || isAddressDocumentPending)
  )

  const hasActiveCorporateDocument = generalStatusData?.categoryStatuses.some(
    (document) =>
      document.category.id === DocumentCategoryType.Corporate &&
      (isCompanyDocumentApproved || isCompanyDocumentPending)
  )

  const isCompanyDocumentUploadAllowed = isAccountCompanyInfoDocumentAllowedUpload(account)

  const isHeaderStatusPending =
    generalStatusData?.categoryStatuses.some(
      (document) => document.status.id === DocumentStatus.PendingApproval
    ) ?? false

  const documentCategories = new Set<DocumentCategoryType>()
  generalStatusData?.categoryStatuses.map((document) =>
    documentCategories.add(document.category.id)
  )

  const someDocumentsPending =
    generalStatusData?.categoryStatuses.some(
      (document) => document.status.id === DocumentStatus.PendingApproval
    ) || isAccountPendingDocumentVerificationStatusType(account?.status.id || -1)

  const requiresAdditionalDocuments =
    generalStatusData?.categoryStatuses.some(
      (document) => document.category.id === DocumentCategoryType.Additional
    ) ?? false

  const isAddressPending = isAddressDocumentPending && !isAddressDocumentApproved
  const isPersonalPending = isPersonalDocumentPending && !isPersonalDocumentApproved
  const isAdditionalPending = isAdditionalDocumentPending && !isAdditionalDocumentApproved
  const isCompanyPending = isCompanyDocumentPending && !isCompanyDocumentApproved

  const requiredDocuments = generalStatusData?.categoryStatuses
    ? generalStatusData.categoryStatuses.length
    : 2

  const hasSufficientDocuments = (): boolean => {
    const isAddressAndPersonalDocsConditionMet =
      (isAddressDocumentApproved || isAddressDocumentPending) &&
      (isPersonalDocumentApproved || isPersonalDocumentPending)

    if (requiredDocuments === 2 && isAddressAndPersonalDocsConditionMet) {
      return false
    }

    const isAdditionalDocConditionMet = isAdditionalDocumentPending || isAdditionalDocumentApproved
    const isCompanyDocConditionMet = isCompanyDocumentPending || isCompanyDocumentApproved

    if (requiredDocuments === 3) {
      if (
        requiresAdditionalDocuments &&
        isAddressAndPersonalDocsConditionMet &&
        isAdditionalDocConditionMet
      ) {
        return false
      }

      if (
        isCompanyDocumentUploadAllowed &&
        isAddressAndPersonalDocsConditionMet &&
        isCompanyDocConditionMet
      ) {
        return false
      }
    }

    if (
      requiredDocuments === 4 &&
      isCompanyDocumentUploadAllowed &&
      requiresAdditionalDocuments &&
      isAddressAndPersonalDocsConditionMet &&
      isAdditionalDocConditionMet &&
      isCompanyDocConditionMet
    ) {
      return false
    }

    if (isCompanyDocumentUploadAllowed) {
      if (requiredDocuments <= 2 && someDocumentsPending) {
        return true
      }
      if (requiredDocuments === 3 && someDocumentsPending) {
        return true
      }
      if (requiredDocuments === 4 && someDocumentsPending && requiresAdditionalDocuments) {
        return true
      }
    } else {
      if (requiredDocuments <= 1 && someDocumentsPending) {
        return true
      }
      if (
        requiredDocuments === 2 &&
        ((isPersonalPending && !isAddressPending && !isAdditionalPending && !isCompanyPending) ||
          (!isPersonalPending && isAddressPending && !isAdditionalPending && !isCompanyPending) ||
          (!isPersonalPending && !isAddressPending && isAdditionalPending && !isCompanyPending) ||
          (!isPersonalPending && !isAddressPending && !isAdditionalPending && isCompanyPending))
      ) {
        return true
      }
      if (requiredDocuments === 2 && someDocumentsPending) {
        return true
      }
      if (requiredDocuments === 3 && someDocumentsPending) {
        return true
      }
    }
    return false
  }

  const isHeaderStatusRejected =
    generalStatusData?.categoryStatuses.some(
      (document) => document.status.id === DocumentStatus.Rejected
    ) ?? false

  const isRejectedWithApprovedDoc = () => {
    const hasRejectedDocument =
      generalStatusData?.categoryStatuses.some(
        (document) => document.status.id === DocumentStatus.Rejected
      ) ?? false

    const hasApprovedDocument =
      generalStatusData?.categoryStatuses.some(
        (document) => document.status.id === DocumentStatus.Approved
      ) ?? false

    return hasRejectedDocument && hasApprovedDocument
  }

  const isPendingWithApprovedDoc = () => {
    const hasPending =
      generalStatusData?.categoryStatuses.some(
        (document) => document.status.id === DocumentStatus.PendingApproval
      ) ?? false
    const hasApprovedDocument =
      generalStatusData?.categoryStatuses.some(
        (document) => document.status.id === DocumentStatus.Approved
      ) ?? false

    return hasPending && hasApprovedDocument
  }

  const isAddressLocked = hasActiveAddressDocument || (account?.isAddressInfoLocked ?? false)
  const isPaymentInfoLocked = account?.isPaymentInfoLocked ?? false
  const isPersonalLocked =
    !isAccountDocumentManagement2MonthExpired(account) &&
    (hasActivePersonalDocument || (account?.isPersonalInfoLocked ?? false))
  const isAdditionalLocked = account?.isAdditionalInfoLocked ?? true
  const isCorporateLocked = hasActiveCorporateDocument || (account?.isCompanyInfoLocked ?? true)

  const getDocumentUploadModalHandler = (id: number) => {
    switch (id) {
      case DocumentCategoryType.Personal:
        return handleIdentificationUploadClick
      case DocumentCategoryType.Address:
        return handleProofOfAddressUploadClick
      case DocumentCategoryType.Corporate:
        return handleCompanyProofUploadClick
      case DocumentCategoryType.Additional:
        return handleAdditionalDocumentsUploadClick
      case DocumentCategoryType.Payment:
        return handlePaymentsUploadClick
      default:
        return handleAdditionalDocumentsUploadClick
    }
  }

  const isLocked = (id: number) => {
    switch (id) {
      case DocumentCategoryType.Personal:
        return isPersonalLocked
      case DocumentCategoryType.Address:
        return isAddressLocked
      case DocumentCategoryType.Corporate:
        return isCorporateLocked
      case DocumentCategoryType.Additional:
        return isAdditionalLocked
      case DocumentCategoryType.Payment:
        return isPaymentInfoLocked
    }
  }

  return (
    <div className={styles.card}>
      <Loading isLoading={isLoading} showLoadingIcon>
        <CardHeader generalStatus={generalStatusData?.generalStatus} />
        {generalStatusData?.categoryStatuses.map((document, index) => {
          return (
            <DocumentUpload
              text={t(document.category.title)}
              documentStatusId={document.status.id}
              documentIndex={index + 1}
              onClick={getDocumentUploadModalHandler(document.category.id)}
              isLocked={isLocked(document.category.id)}
              key={`documentUploadCategory-${document.category.id}`}
            />
          )
        })}
        <CardMessage
          isHeaderStatusPending={isHeaderStatusPending}
          isHeaderStatusRejected={isHeaderStatusRejected}
          hasSufficientDocuments={hasSufficientDocuments()}
          requiresAdditionalDocuments={requiresAdditionalDocuments}
          isRejectedWithApprovedDoc={isRejectedWithApprovedDoc()}
          isPendingWithApprovedDoc={isPendingWithApprovedDoc()}
          documentCategories={documentCategories}
        />
      </Loading>
    </div>
  )
}

interface PersonalDocumentUploadProps {
  text: string
  onClick(): void
  documentStatusId: number
  documentIndex: number
  isLocked?: boolean
}

const DocumentUpload: FC<PersonalDocumentUploadProps> = ({
  text,
  documentStatusId,
  documentIndex,
  onClick,
  isLocked,
}) => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const isMobile = useWindowResize()

  const is1MonthExpired = isAccountDocumentManagement1MonthExpired(account)
  const is2MonthExpired = isAccountDocumentManagement2MonthExpired(account)

  const DocIcon = () => {
    if (documentStatusId === DocumentStatus.PendingApproval) {
      return <CircleCheckIcon color='text-warning-high-em' />
    }
    if (documentStatusId === DocumentStatus.Approved) {
      if (is1MonthExpired || is2MonthExpired) {
        return (
          <RichIcon
            bg='surface-info-high-em'
            label={documentIndex.toString()}
            labelColor='text-white'
            shape='circle'
            size='x24'
          />
        )
      }
      return <CircleCheckIcon color='text-success-high-em' />
    }
    if (documentStatusId === DocumentStatus.Rejected) {
      return <CloseIcon color='text-danger-high-em' />
    }
    return (
      <RichIcon
        bg='surface-info-high-em'
        label={documentIndex.toString()}
        labelColor='text-white'
        shape='circle'
        size='x24'
      />
    )
  }

  const DocButton = () => {
    if (isLocked && !is1MonthExpired && !is2MonthExpired) {
      return null
    }

    if (isMobile) {
      return <IconButton icon={CirclePlusIcon} onClick={onClick} />
    }

    return (
      <Button.Primary size='sm' onClick={onClick}>
        {t('Profile.Upload')}
      </Button.Primary>
    )
  }

  return (
    <div className={styles.secondaryCard}>
      <div className='is-flex is-justify-content-space-between'>
        <div className='is-flex is-align-items-center'>
          <DocIcon />
          <Body2 className={classNames(styles.textSecondary, styles.buttonText)}>{text}</Body2>
        </div>
        <div>
          <DocButton />
        </div>
      </div>
    </div>
  )
}

interface CardMessageProps {
  isHeaderStatusRejected: boolean
  isHeaderStatusPending: boolean
  requiresAdditionalDocuments: boolean
  hasSufficientDocuments: boolean
  isRejectedWithApprovedDoc: boolean
  isPendingWithApprovedDoc: boolean
  documentCategories: Set<DocumentCategoryType>
}

const CardMessage: FC<CardMessageProps> = ({
  isHeaderStatusRejected,
  isHeaderStatusPending,
  requiresAdditionalDocuments,
  hasSufficientDocuments,
  isRejectedWithApprovedDoc,
  isPendingWithApprovedDoc,
  documentCategories,
}) => {
  const { t } = useTranslation()

  const getMessage = () => {
    if (isHeaderStatusRejected && !isRejectedWithApprovedDoc) {
      return (
        <>
          <p>{t('Profile.It appears that some of the documents you uploaded were rejected')}</p>
          <p className='mt-2'>
            {t(
              'Profile.Meanwhile, please make sure that the file name of your document contains only English characters'
            )}
          </p>
        </>
      )
    }
    if (isPendingWithApprovedDoc) {
      return t('Profile.Document reviewing may take up to 24 working hours to complete')
    }
    if (isHeaderStatusRejected && isRejectedWithApprovedDoc) {
      return t('Profile.It appears that some of the documents you uploaded were rejected')
    }
    if (isHeaderStatusPending && !isPendingWithApprovedDoc) {
      return null
    }
    return t(
      'Profile.Meanwhile, please make sure that the file name of your document contains only English characters'
    )
  }

  return (
    <>
      <Body2 className={styles.textSecondary}>{getMessage()}</Body2>
      {!isPendingWithApprovedDoc &&
        ((isHeaderStatusRejected && !isRejectedWithApprovedDoc) ||
          (!requiresAdditionalDocuments &&
            !isRejectedWithApprovedDoc &&
            !isPendingWithApprovedDoc) ||
          !documentCategories.size ||
          !(isHeaderStatusPending && !isPendingWithApprovedDoc) ||
          (hasSufficientDocuments && !isRejectedWithApprovedDoc && !isPendingWithApprovedDoc)) && (
          <>
            <br />
            <br />
            <Body2 className={styles.textSecondary}>
              {t('Profile.Document reviewing may take up to 24 working hours to complete')}
            </Body2>
          </>
        )}
    </>
  )
}

interface CardHeaderProps {
  generalStatus?: NameDto
}

const CardHeader: FC<CardHeaderProps> = ({ generalStatus }) => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()

  const is1MonthExpired = isAccountDocumentManagement1MonthExpired(account)
  const is2MonthExpired = isAccountDocumentManagement2MonthExpired(account)

  const IconComponent = () => {
    if (generalStatus) {
      if (isDocumentsRequiredGeneralStatus(generalStatus.id)) {
        return <AlertIcon color='text-info-med-em' />
      }
      if (isVerificationPendingGeneralStatus(generalStatus.id)) {
        return <AlertIcon color='text-warning-high-em' />
      }
      if (isRejectedDocumentsGeneralStatus(generalStatus.id)) {
        return <AlertIcon color='text-danger-high-em' />
      }
      if (
        isDocumentsApprovedGeneralStatus(generalStatus.id) &&
        (is1MonthExpired || is2MonthExpired)
      ) {
        return <AlertIcon color='text-info-med-em' />
      }
      if (isDocumentsApprovedGeneralStatus(generalStatus.id)) {
        return <CircleCheckIcon color='text-success-high-em' />
      }
      if (isMoreDocumentsRequiredGeneralStatus(generalStatus.id)) {
        return <AlertIcon color='text-info-med-em' />
      }
      if (isAdditionalDocumentsGeneralStatus(generalStatus.id)) {
        return <AlertIcon color='text-info-med-em' />
      }
    }

    return <AlertIcon color='text-warning-high-em' />
  }

  const headerMessage = useMemo(() => {
    if (generalStatus) {
      if (isDocumentsRequiredGeneralStatus(generalStatus.id)) {
        return `${t('Profile.Documents Required')}!`
      }
      if (isRejectedDocumentsGeneralStatus(generalStatus.id)) {
        return `${t('Profile.Rejected Documents')}!`
      }
      if (isVerificationPendingGeneralStatus(generalStatus.id)) {
        return t('Profile.Verification Pending')
      }
      if (isAdditionalDocumentsGeneralStatus(generalStatus.id)) {
        return t('Profile.Additional Documents')
      }
      if (isMoreDocumentsRequiredGeneralStatus(generalStatus.id)) {
        return `${t('Profile.More Documents Required')}!`
      }
    }

    return `${t('Profile.Verification Pending')}!`
  }, [t, generalStatus])

  const getAdditionalMessage = (): string | null => {
    if (!generalStatus) {
      return null
    }

    if (isDocumentsRequiredGeneralStatus(generalStatus.id)) {
      return `${t('Profile.In order to activate your Client Area and start trading')}:`
    }

    if (isAdditionalDocumentsGeneralStatus(generalStatus.id)) {
      return t(
        'Profile.Please note that we are requesting additional documents related to your application. You can find more information about it in the email we sent you'
      )
    }

    if (isMoreDocumentsRequiredGeneralStatus(generalStatus.id)) {
      return `${t(
        'Profile.Please note that the following document is still waiting to be uploaded'
      )}:`
    }

    return null
  }

  return (
    <>
      <div className='is-flex is-align-items-center'>
        <IconComponent />
        <Heading6 className={styles.headerText} medium>
          {headerMessage}
        </Heading6>
      </div>
      <Body2 isParagraph className={styles.additionalMessage}>
        {getAdditionalMessage()}
      </Body2>
    </>
  )
}
